<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui'

export interface FavoriteStoreOption {
  id: number
  slug: string
  store_name: string
}

const props = withDefaults(defineProps<{
  modelValue: string | null
  availableStores?: FavoriteStoreOption[]
  persistKey?: string
  buttonColor?: 'secondary' | 'tertiary' | 'primary' | 'neutral'
}>(), {
  availableStores: () => [],
  buttonColor: 'secondary',
})

const emit = defineEmits<{
  'update:modelValue': [value: string | null]
  'change': [value: string | null]
}>()

const cacheStore = useCacheStore()
const localePath = useLocalePath()

type FavoriteStoresMenuItem = DropdownMenuItem & {
  storeSlug?: string
}

const favoriteStoresCount = computed(() => props.availableStores.length)

const buttonBadgeCount = computed(() =>
  props.modelValue == null ? favoriteStoresCount.value : 1,
)

const menuItems = computed((): FavoriteStoresMenuItem[][] => {
  const allStoresItem: FavoriteStoresMenuItem = {
    label: 'Mes commerces favoris',
    type: 'checkbox',
    checked: props.modelValue == null,
    onUpdateChecked(checked: boolean) {
      if (checked) {
        selectStore(null)
      }
    },
    onSelect(e: Event) {
      e.preventDefault()
    },
  }

  const storeItems: FavoriteStoresMenuItem[] = props.availableStores.map((store) => ({
    label: store.store_name,
    storeSlug: store.slug,
    type: 'checkbox' as const,
    checked: props.modelValue === store.slug,
    onUpdateChecked(checked: boolean) {
      if (checked) {
        selectStore(store.slug)
      }
    },
    onSelect(e: Event) {
      e.preventDefault()
    },
  }))

  return [[allStoresItem, ...storeItems]]
})

function selectStore(storeSlug: string | null) {
  emit('update:modelValue', storeSlug)
  if (props.persistKey) {
    cacheStore.setCache(props.persistKey, storeSlug)
  }
  emit('change', storeSlug)
}

watch(() => props.modelValue, (value) => {
  if (props.persistKey) {
    cacheStore.setCache(props.persistKey, value)
  }
})
</script>

<template>
  <UDropdownMenu
    :items="menuItems"
    checked-icon="i-mdi-check"
    :content="{ align: 'start', sideOffset: 1 }"
    :ui="{ content: 'w-72 max-h-80 overflow-y-auto' }"
    class="group"
    
  >
    <UButton
      trailing-icon="i-mdi-menu-down"
      :color="buttonColor"
      :ui="{
        trailingIcon: 'group-data-[state=open]:rotate-180 transition-transform duration-200',
      }"
    >
      Commerces
      <UBadge size="sm" variant="soft" color="white">
        {{ buttonBadgeCount }}
      </UBadge>
    </UButton>
    <template #item-label="{ item }">
      <span class="flex w-full min-w-0 items-center justify-between gap-2">
        <span class="truncate">{{ item.label }}</span>
        <UBadge
          v-if="item.storeSlug == null"
          size="sm"
          variant="soft"
          color="secondary"
        >
          {{ favoriteStoresCount }}
        </UBadge>
      </span>
    </template>
    <template #content-bottom>
      <div class="border-t border-default p-2">
        <UButton
          block
          variant="soft"
          color="secondary"
          label="Modifier mes commerces favoris"
          :to="localePath({ name: 'UserProfilePage' })"
        />
      </div>
    </template>
  </UDropdownMenu>
</template>
